import { Input } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import "./DashboardModelsCard.css";
import ModelsTab from "./ModelsTab";
import GeneratedModels from "./GeneratedModels";
import DraftModels from "./DraftModels";
import search from "../../../../assets/icons/ModelSearch.svg";
import listView from "../../../../assets/icons/list.svg";
import fullView from "../../../../assets/icons/fullview.svg";
import smallGridView from "../../../../assets/icons/smallgridview.svg";
import largeGridView from "../../../../assets/icons/largegridview.svg";
import deleteIcon from "../../../../assets/icons/Delete-transparent.svg";
import selectIcon from "../../../../assets/icons/select-all.svg";
import useDebounce from "../../../../hooks/useDebounce";
import { useDispatch, useSelector } from "react-redux";
import { DraftModelTypes, GeneratedModelTypes } from "../../../../utilities/types/ModelsTypes";
import { RootState } from "../../../../service/store";
import { useDeleteModelListsMutation } from "../../../../service/apiSlices/dashboard";
import { setSelectModel, updateCountGeneratedModels, setSelectedKeys } from "../../../../service/apiSlices/staticApi";
import { ActionItemProps } from "../../../../utilities/types/DashboardTypes";
import DeleteConfirmModel from "../DeleteOpenModel/DeleteConfirmModel";
import { TranslatedConstants } from "../../../../utilities/Constants/TranslateConstants";
import { useTranslation } from "react-i18next";
import { getActionsHighlightPosition, getButtonState, getFilterHighlightPosition, getHighlightPosition } from "../../../../utilities/utils";
import { showSuccessToast, showWarningToast } from "../../../../utilities/toastUtils";

const DashboardModelsCard = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { countGeneratedModels } = useSelector((state: any) => state.static);
  const { modelsType } = TranslatedConstants();
  const [activeTab, setActiveTab] = useState("generated");
  const [searchValue, setSearchValue] = useState("");
  const [generatedCount, setGeneratedCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [activeView, setActiveView] = useState<any>('smallGrid');
  const [hoveredView, setHoveredView] = useState<any>(null);
  const [actionsActive, setActionsActive] = useState<any>(null);
  const [actionsHovered, setActionsHovered] = useState<any>(null);
  const [activeFilter, setActiveFilter] = useState<any>('all');
  const [hoveredFilter, setHoveredFilter] = useState<any>(null);
  const debouncedSearchText = useDebounce(searchValue);
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
  const [deleteModelList] = useDeleteModelListsMutation();

  const { selectedKeys, generatedList, draftList, selectModel }: { selectedKeys: any, generatedList: GeneratedModelTypes[], draftList: DraftModelTypes[], selectModel: any } = useSelector((state: RootState) => {
    const { generatedList, ...rest } = state?.static;
    return {
      ...rest,
      generatedList: generatedList.map((item: any) => ({
        filename: item.filename || '',
        workspace_name: item.workspace_name || '',
        created_on: item.created_on || '',
        secret_key: item.secret_key || '',
        permission: item.permission || '',
        ...item,
      })),
    };
  });
  const list = activeTab === "draft" ? draftList : generatedList;

  // Handle global state changes
  const handleSelectModelChange = (value: boolean) => {
    dispatch(setSelectModel(value));
  };

  const handleOnActionClick = (key: ActionItemProps["key"]) => {

    switch (key) {
      case "check":
        setIsActive(pre => !pre)
        if (generatedList?.length > 0 || draftList?.length > 0) {
          handleSelectModelChange(!selectModel);
          if (selectModel) {
            dispatch(setSelectedKeys([]));
          }
        }
        break;

      case "delete":
        if (selectedKeys?.length > 0) {
          setIsDeleteModelOpen(true);
        }
        break;

      case "merge":
        if (selectedKeys?.length > 1) {
          // Handle merge action
        }
        break;
    }
  };

  const handleDelete = () => {
    const payload = {
      secret_key: selectedKeys
    };

    deleteModelList(payload)
      .unwrap()
      .then((response: any) => {
        if (response?.status_code === 200) {
          dispatch(updateCountGeneratedModels());
          setIsDeleteModelOpen(false);
          handleSelectModelChange(false);
          setActionsActive(null);
          setIsActive(false);
          dispatch(setSelectedKeys([]));

          if (response?.data?.still_embedding?.length > 0) {
            showWarningToast(response?.message);
          } else {
            showSuccessToast(response?.message);
          }
        } else if (response?.status_code === 400) {
          showWarningToast(response?.message);
          dispatch(updateCountGeneratedModels());
          setIsDeleteModelOpen(false);
          handleSelectModelChange(false);
          setActionsActive(null);
          setIsActive(false);
          dispatch(setSelectedKeys([]));
        }
      })
      .catch((error: any) => {
        console.error('Delete operation failed:', error);
        showWarningToast('Delete operation failed');
      });
  };

  const generatedModelCount = (count: number) => {
    setGeneratedCount(count);
  };

  const draftModelCount = (count: number) => {
    setDraftCount(count);
  };

  const renderTabContent = (type: string) => {
    switch (type) {
      case "generated":
        setIsActive(false);
        dispatch(setSelectModel(false));
        return (
          <GeneratedModels
            type={type}
            activeFilter={activeFilter}
            isActive={activeTab === type}
            search={debouncedSearchText}
            getGeneratedModelCount={generatedModelCount}
          />
        );
      case "draft":
        setIsActive(false);
        dispatch(setSelectModel(false));
        
        return (
          <DraftModels
            type={type}
            activeFilter={activeFilter}
            isActive={activeTab === type}
            search={debouncedSearchText}
            getDraftModelCount={draftModelCount}
            key={0}
          />
        );
      default:
        return null;
    }
  };

  const tabs = useMemo(() => {
    return modelsType?.map((model) => ({
      ...model,
      content: renderTabContent(model.key),
    }));
  }, [activeTab, activeFilter, debouncedSearchText, countGeneratedModels]);

  const handleSearchValue = (e: any) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    handleTabChange("generated")
  }
    , [countGeneratedModels]);

  const handleTabChange = useCallback(
    (key: string) => {
      // Reset all states when changing tabs
      setActiveTab(key);
      setIsActive(false);
      setActionsActive(null);
      handleSelectModelChange(false);
      dispatch(setSelectedKeys([]));
      setActiveFilter("all");
    }
    , [countGeneratedModels, activeTab, activeFilter, debouncedSearchText]);

  return (
    <div className="">
      <div className="row">
        <div className="col-md-12">
          <ModelsTab
            activeTab={activeTab}
            tabs={tabs}
            onTabChange={handleTabChange}
            titleActions={
              <>
                <div className="d-flex align-items-center justify-content-between gap-2 models-actions-wrapper">
                  <div
                    className="grid-view-button-wrapper"
                    onMouseLeave={() => setHoveredView(null)}
                  >
                    <div
                      className="button-highlight"
                      style={{
                        transform: `translateX(${getHighlightPosition(hoveredView, activeView)}px)`,
                        opacity: hoveredView || activeView ? 1 : 0
                      }}
                    />
                    <div className="list-button-wrapper">
                      <button
                        className={`list-view-button disabled ${activeView === 'list' ? 'active' : ''}`}
                        onClick={() => setActiveView('list')}
                        onMouseEnter={() => setHoveredView('list')}
                        disabled
                      >
                        <img src={listView} width={20} alt="list view" />
                      </button>
                    </div>
                    <div className="full-view-button-wrapper">
                      <button
                        className={`full-view-button disabled ${activeView === 'full' ? 'active' : ''}`}
                        onClick={() => setActiveView('full')}
                        onMouseEnter={() => setHoveredView('full')}
                        disabled
                      >
                        <img src={fullView} width={20} alt="full view" />
                      </button>
                    </div>
                    <div className="small-grid-view-button-wrapper">
                      <button
                        className={`small-grid-view-button ${activeView === 'smallGrid' ? 'active' : ''}`}
                        onClick={() => setActiveView('smallGrid')}
                        onMouseEnter={() => setHoveredView('smallGrid')}
                      >
                        <img src={smallGridView} width={20} alt="small grid view" />
                      </button>
                    </div>
                    <div className="large-grid-view-button-wrapper">
                      <button
                        className={`large-grid-view-button disabled ${activeView === 'largeGrid' ? 'active' : ''}`}
                        onClick={() => setActiveView('largeGrid')}
                        onMouseEnter={() => setHoveredView('largeGrid')}
                        disabled
                      >
                        <img src={largeGridView} alt="large grid view" />
                      </button>
                    </div>
                  </div>
                  <div className="">
                    <Input
                      placeholder="Search models"
                      variant="borderless"
                      className="dashboard-search-model-input"
                      prefix={<img src={search} alt="search" width={15} />}
                      value={searchValue}
                      onChange={handleSearchValue}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="modelCardDivider me-2"></div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-3 mt-2 ps-3">
                  <div className="model-filter-wrapper"
                    onMouseLeave={() => setHoveredFilter(null)}
                  >
                    <div
                      className="filter-button-highlight"
                      style={{
                        transform: `translateX(${getFilterHighlightPosition(hoveredFilter, activeFilter)}px)`,
                        opacity: hoveredFilter || activeFilter ? 1 : 0
                      }}
                    />
                    <div className="all-models-button-wrapper">
                      <button
                        className={`all-models-button ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('all')}
                        onMouseEnter={() => setHoveredFilter('all')}
                      >
                        {t('all')}
                      </button>
                    </div>
                    <div className="created-by-me-button-wrapper">
                      <button
                        className={`created-by-me-button ${activeFilter === 'created' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('created')}
                        onMouseEnter={() => setHoveredFilter('created')}
                      >
                        {t('createdByMe')}
                      </button>
                    </div>
                    {activeTab === "generated" && (
                      <>
                        {/* <div className="team-models-button-wrapper">
                          <button
                            className={`team-models-button ${activeFilter === 'team' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('team')}
                            onMouseEnter={() => setHoveredFilter('team')}
                          >
                            {t('teamModels')}
                          </button>
                        </div> */}
                        <div className="shared-models-button-wrapper">
                          <button
                            className={`shared-models-button ${activeFilter === 'shared' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('shared')}
                            onMouseEnter={() => setHoveredFilter('shared')}
                          >
                            {t('sharedModels')}
                          </button>
                        </div>
                      </>)}
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-2 pe-3">
                    <div className="select-all-button-wrapper">
                      <button className={`select-all-button ${isActive ? "active" : ""}`}
                        style={{
                          opacity: getButtonState(list, selectModel, selectedKeys, 'check').opacity,
                          cursor: list?.length === 0 ? 'not-allowed' : getButtonState(list, selectModel, selectedKeys, 'check').cursor
                        }}
                        disabled={list?.length === 0}
                        onClick={() => handleOnActionClick('check')}
                      >
                        <img src={selectIcon} alt="select" />
                        <span className="dashboard-secoundary-black">{t('select')}</span>
                      </button>
                    </div>
                    <div className="model-actions-wrapper"
                      onMouseLeave={() => setActionsHovered(null)}
                    >
                      <div
                        className="action-button-highlight"
                        style={{
                          transform: `translateX(${getActionsHighlightPosition(actionsHovered, actionsActive)}px)`,
                          opacity: actionsHovered || actionsActive ? 1 : 0
                        }}
                      />
                      {/* <div className="merge-button-wrapper">
                      <button className={`merge-button ${actionsActive === 'merge' ? 'active' : ''}`}
                        onMouseEnter={() => setActionsHovered('merge')}
                        onClick={() => setActionsActive('merge')}
                        style={{ opacity: getButtonState(list, selectModel, selectedKeys, 'merge').opacity, cursor: getButtonState(list, selectModel, selectedKeys, 'merge').cursor }}
                        disabled={!getButtonState(list, selectModel, selectedKeys, 'merge').isEnabled}
                      >
                        <img src={mergeIcon} width={20} alt="merge" />
                        {t('merge')}
                      </button>
                    </div> */}
                      <div className="delete-button-wrapper">
                        <button className={`delete-button ${actionsActive === 'delete' ? 'active' : ''}`}
                          onMouseEnter={() => setActionsHovered('delete')}
                          onClick={() => { setActionsActive('delete'); handleOnActionClick('delete') }}
                          style={{ opacity: getButtonState(list, selectModel, selectedKeys, 'delete').opacity, cursor: getButtonState(list, selectModel, selectedKeys, 'delete').cursor }}
                          disabled={!getButtonState(list, selectModel, selectedKeys, 'delete').isEnabled}
                        >
                          <img src={deleteIcon} width={18} alt="delete" />
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            }
          />
        </div>
      </div>
      <DeleteConfirmModel
        visible={isDeleteModelOpen}
        onConfirm={handleDelete}
        onCancel={() => { setIsDeleteModelOpen(false); setActionsActive(null); }}
        title={t('deleteModal.title')}
        content={t('deleteModal.description')}
        okText={t('buttons.confirm')}
        cancelText={t('buttons.cancel')}
      />
    </div>
  );
};

export default DashboardModelsCard;